import { useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'

import FeaturedTable from '../../FeaturedTable/FeaturedTable'
import { getAmountToRefund } from '../utils'

import Loader from './Loader'
import StatusPill from './StatusPill'
import RowActions from './RowActions'
import TicketPrice from './TicketPrice'
import ConfirmDeleteModal from './ConfirmDeleteModal'
import AttendeeName from './AttendeeName'

const Table = ({
  attendees,
  isLoading,
  isError,
  onDeleteAttendee,
  onCheckInAttendee,
  canCheckInAttendee,
}) => {
  const { t } = useTranslation(['attendees'])
  const [attendeeToDelete, setAttendeeToDelete] = useState(null)

  const columns = useMemo(
    () => [
      {
        Header: t('attendees:columns.attendee'),
        accessor: 'user',
        Cell: ({ row }) => {
          return (
            <AttendeeName
              avatarUrl={row.original.user.avatar}
              firstLine={row.original.user.name}
              secondLine={row.original.user.apartment}
            />
          )
        },
      },
      {
        Header: t('attendees:columns.email'),
        accessor: 'user.email',
        Cell: ({ row }) => {
          return row.original.user.email || '-'
        },
      },
      {
        Header: t('attendees:columns.phone'),
        accessor: 'user.phone',
        Cell: ({ row }) => {
          return row.original.user.phone || '-'
        },
      },
      {
        Header: t('attendees:columns.payer'),
        accessor: 'payer',
        Cell: ({ row }) => {
          return row.original.payer || '-'
        },
      },
      {
        Header: t('attendees:columns.ticketType'),
        accessor: 'ticketType',
      },
      {
        Header: t('attendees:columns.ticketPrice'),
        accessor: 'ticketPrice',
        Cell: ({ row }) => {
          const { ticketPrice, refundedAmount } = row.original
          return (
            <TicketPrice id={row.original.id} price={ticketPrice} refundedAmount={refundedAmount} />
          )
        },
      },
      {
        Header: t('attendees:columns.status'),
        accessor: 'status',
        Cell: ({ row }) => {
          const { id, status, refundedAmount } = row.original
          return <StatusPill id={id} status={status} refundedAmount={refundedAmount} />
        },
      },
      {
        Header: '',
        accessor: 'actions',
        Cell: ({ row }) => {
          const isRemoved = row.original.isRemoved
          const isCheckedIn = row.original.isCheckedIn

          const isCheckInDisabled = !canCheckInAttendee || isRemoved || isCheckedIn

          const amountToRefund = getAmountToRefund(row.original.ticketPrice, row.original.guests)

          if (!row.original.id) {
            return null
          }

          const attendeeIds = []
          let guestIds = []

          if (row.original.user.role === 'Guest') {
            guestIds.push(row.original.id)
          } else {
            attendeeIds.push(row.original.id)
            const attendeeGuestIds = row.original.guests
              .filter((guest) => !guest.isRemoved && !guest.isRefunded)
              .map((guest) => guest.id)
            guestIds = attendeeGuestIds
          }

          return (
            <RowActions
              isCheckInDisabled={isCheckInDisabled}
              onCheckInClick={() => {
                onCheckInAttendee(attendeeIds, guestIds)
              }}
              isDeleteDisabled={isRemoved || isCheckedIn}
              onDeleteClick={() => {
                setAttendeeToDelete({
                  id: row.original.id,
                  name: row.original.user.name,
                  role: row.original.user.role,
                  hasGuests: row.original.guests ? !!(row.original.guests || []).length : false,
                  isFreeTicket: !amountToRefund,
                  amountToRefund,
                })
              }}
            />
          )
        },
      },
    ],
    [attendees, canCheckInAttendee, onCheckInAttendee, onDeleteAttendee, t],
  )

  if (isLoading) {
    return <Loader />
  }

  if (isError) {
    return <p className="attendees_error-message">{t('attendees:errors.statisticsError')}</p>
  }

  const handleDeleteConfirm = (payerId, attendeeRole, withRefund) => {
    onDeleteAttendee(payerId, attendeeRole, withRefund)
    setAttendeeToDelete(null)
  }

  return (
    <div className="attendees_table attendees_table--main">
      <FeaturedTable
        data={attendees}
        columns={columns}
        getRowClassName={(row) => {
          return row.original.user.role === 'Guest' ? 'attendees-guest-row' : ''
        }}
        getRowId={(row, index) => `${row.id}-${index}`}
      />

      {attendeeToDelete && (
        <ConfirmDeleteModal
          attendeeToDelete={attendeeToDelete}
          onClose={() => setAttendeeToDelete(null)}
          onConfirm={handleDeleteConfirm}
        />
      )}
    </div>
  )
}

Table.propTypes = {
  attendees: PropTypes.array,
  isLoading: PropTypes.bool,
  isError: PropTypes.bool,
  canCheckInAttendee: PropTypes.bool,
  onDeleteAttendee: PropTypes.func,
  onCheckInAttendee: PropTypes.func,
}

export default Table
